import { Stack, Typography, Collapse } from '@mui/material';
import fr from 'date-fns/locale/fr';
import { isEmpty } from 'lodash';
import { useParams } from 'next/navigation';
import React from 'react';
import { Controller, Control } from 'react-hook-form';
import { useGetCartLocks } from 'src/api/hooks/cart-lock';
import { useTranslate } from 'src/locales';
import { CartItemDTO } from 'src/types';

import { CurrencyCodes } from '../../types/users/currency';
import { formatCurrency } from '../../utils/formatters/currency';
import IncrementButton from './Increment-button';
import TicketInfoHeader from './ticket-info-header';

type TicketAvailableProps = {
  ticket: CartItemDTO;
  index: number;
  control: Control<any>;
  handleIncrease: (field: any, index: number) => void;
  handleDecrease: (field: any, index: number) => void;
  mdUp?: boolean;
  currency?: CurrencyCodes;
};
type TicketStatusProps = {
  from?: string;
  to?: string;
};

// TicketStatus Component
const TicketStatus = ({ from, to }: TicketStatusProps) => {
  const { t: tEvent, i18n } = useTranslate('event');
  const now = new Date();

  // Parse dates and treat them as local time
  const saleStartsAt = from ? new Date(from) : new Date();
  const saleEndsAt = to ? new Date(to) : new Date();

  // Adjust for timezone difference from UTC
  const offsetHours = new Date().getTimezoneOffset() / 60;

  // Adjust dates
  saleStartsAt.setHours(saleStartsAt.getHours() - offsetHours);
  saleEndsAt.setHours(saleEndsAt.getHours() - offsetHours);

  // Determine which locale to use for date formatting
  const locale = i18n?.language === 'fr' ? fr : undefined;

  if (now < saleStartsAt) {
    // Calculate time difference in milliseconds
    const timeDiff = saleStartsAt.getTime() - now.getTime();

    // Convert to minutes first (most precise unit we're using)
    const totalMinutes = Math.floor(timeDiff / (1000 * 60));

    // Calculate hours and remaining minutes
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    // For debugging
    console.log('Time remaining:', { totalMinutes, hours, minutes });

    // Determine appropriate message based on time remaining
    let salesStartText = '';

    if (hours < 1) {
      // Less than an hour, show minutes only
      if (minutes === 1) {
        salesStartText =
          tEvent('ticket-status.sales-start-minute-single') || 'Sales start in 1 minute ⏱️';
      } else {
        salesStartText =
          tEvent('ticket-status.sales-start-minutes', {
            count: minutes,
          }) || `Sales start in ${minutes} minutes ⏱️`;
      }
    } else if (hours === 1 && minutes === 0) {
      // Exactly 1 hour
      salesStartText =
        tEvent('ticket-status.sales-start-hour-single') || 'Sales start in 1 hour ⏰';
    } else if (hours === 1) {
      // 1 hour and some minutes
      salesStartText =
        tEvent('ticket-status.sales-start-hour-and-minutes', {
          minutes,
        }) || `Sales start in 1 hour and ${minutes} minutes ⏰`;
    } else {
      // Multiple hours and minutes
      salesStartText =
        tEvent('ticket-status.sales-start-hours-plural', {
          hours,
          minutes,
        }) || `Sales start in ${hours} hours and ${minutes} minutes ⏰`;
    }

    return (
      <Stack alignItems="center" justifyContent="center">
        <Typography variant="body2" color="#c2c2c2">
          {salesStartText}
        </Typography>
      </Stack>
    );
  }

  if (now > saleEndsAt) {
    // Default fallback text in case translation is missing
    const salesEndedText = tEvent('ticket-status.sales-ended') || 'Sales Ended';

    return (
      <Stack alignItems="center" justifyContent="center">
        <Typography variant="body2" color="#c2c2c2">
          {salesEndedText}
        </Typography>
      </Stack>
    );
  }

  // If we're within the sales period, return null so the parent can show the increment button
  return null;
};

const TicketAvailable = ({
  ticket,
  index,
  control,
  handleIncrease,
  handleDecrease,
  currency,
  mdUp,
}: TicketAvailableProps) => {
  const { t: tEvent } = useTranslate('event');
  const { id } = useParams();

  const { data: cartLocks } = useGetCartLocks(Number(id));

  // Current time
  const now = new Date();

  // Parse dates and manually adjust for timezone
  const saleStartsAt = ticket?.from ? new Date(ticket?.from) : null;
  const saleEndsAt = ticket?.to ? new Date(ticket?.to) : null;

  // Adjust for timezone difference from UTC
  let adjustedStartsAt = null;
  let adjustedEndsAt = null;

  if (saleStartsAt || saleEndsAt) {
    const offsetHours = new Date().getTimezoneOffset() / 60;

    // Create new Date objects with adjusted times for comparison
    if (saleStartsAt) {
      adjustedStartsAt = new Date(saleStartsAt);
      adjustedStartsAt?.setHours(adjustedStartsAt.getHours() - offsetHours);
    }

    if (saleEndsAt) {
      adjustedEndsAt = new Date(saleEndsAt);
      adjustedEndsAt?.setHours(adjustedEndsAt.getHours() - offsetHours);
    }
  }

  // Improved check for sale period
  const isWithinSalePeriod =
    (!adjustedStartsAt || now >= adjustedStartsAt) && (!adjustedEndsAt || now <= adjustedEndsAt);

  if (ticket.net === 0) {
    return (
      <Stack
        direction="column"
        sx={{
          backgroundColor: 'rgba(0, 0, 0,0.4)',
          ...(mdUp && { width: '100%' }),
          px: 2,
          py: 2,
          border: '0.1px solid #fff',
          borderRadius: 2,
        }}
      >
        <TicketInfoHeader ticket={ticket} />

        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="flex-start"
            columnGap={2}
            rowGap={1}
            flexWrap="wrap"
          >
            <Typography variant="body1" fontWeight={600}>
              {formatCurrency(ticket.net, currency)}
            </Typography>

            <Collapse
              in={
                isWithinSalePeriod &&
                ticket.quantityLeft <= 10 &&
                ticket.quantityInCart !== ticket.quantityLeft &&
                ticket.quantityInCart !== ticket.maxPerUser &&
                isEmpty(cartLocks)
              }
              timeout={{ appear: 300, enter: 300, exit: 0 }}
              mountOnEnter
              unmountOnExit
            >
              <Typography variant="body2" color="#c2c2c2" sx={{ fontSize: 12 }}>
                {tEvent('ticket-status.limited-tickets-left')}
              </Typography>
            </Collapse>
            <Collapse
              in={isWithinSalePeriod && ticket.quantityInCart === ticket.quantityLeft}
              timeout={{ appear: 300, enter: 300, exit: 0 }}
              mountOnEnter
              unmountOnExit
            >
              <Typography variant="body2" color="#c2c2c2" sx={{ fontSize: 12 }}>
                {tEvent('ticket-status.last-one')}
              </Typography>
            </Collapse>
            <Collapse
              in={isWithinSalePeriod && ticket.quantityInCart === ticket.maxPerUser}
              timeout={{ appear: 300, enter: 300, exit: 0 }}
              mountOnEnter
              unmountOnExit
            >
              <Typography variant="body2" color="#c2c2c2" sx={{ fontSize: 12 }}>
                {tEvent('ticket-status.max-tickets', { maxPerUser: ticket.maxPerUser })}
              </Typography>
            </Collapse>
            <Collapse
              in={
                isWithinSalePeriod &&
                cartLocks &&
                cartLocks.some((lock) => lock.itemId === ticket.id)
              }
              timeout={{ appear: 300, enter: 300, exit: 0 }}
              mountOnEnter
              unmountOnExit
            >
              <Typography variant="body2" color="#c2c2c2" sx={{ fontSize: 12 }}>
                {tEvent('ticket-status.lock-checkout', { maxPerUser: ticket.maxPerUser })}
              </Typography>
            </Collapse>
          </Stack>

          {/* Conditional rendering of either TicketStatus or IncrementButton */}
          {!isWithinSalePeriod ? (
            <TicketStatus
              from={ticket.from ? String(ticket.from) : ''}
              to={ticket.to ? String(ticket.to) : ''}
            />
          ) : (
            <Controller
              name={`tickets.${index}.quantityInCart`}
              control={control}
              render={({ field }) => (
                <IncrementButton
                  quantity={field.value}
                  onIncrease={() => handleIncrease(field, index)}
                  onDecrease={() => handleDecrease(field, index)}
                  disabledDecrease={field.value <= 0}
                />
              )}
            />
          )}
        </Stack>
      </Stack>
    );
  }

  return (
    <Stack
      direction="column"
      sx={{
        backgroundColor: 'rgba(0, 0, 0,0.4)',
        ...(mdUp && { width: '100%' }),
        px: 2,
        py: 2,
        border: '0.1px solid #fff',
        borderRadius: 2,
      }}
    >
      <TicketInfoHeader ticket={ticket} />

      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="flex-start"
          columnGap={2}
          rowGap={1}
          flexWrap="wrap"
        >
          <Typography variant="body1" fontWeight={600}>
            {formatCurrency(ticket.net, currency)}
          </Typography>
          <Collapse
            in={
              isWithinSalePeriod &&
              ticket.quantityLeft <= 10 &&
              ticket.quantityInCart !== ticket.quantityLeft &&
              ticket.quantityInCart !== ticket.maxPerUser &&
              isEmpty(cartLocks)
            }
            timeout={{ appear: 300, enter: 300, exit: 0 }}
            mountOnEnter
            unmountOnExit
          >
            <Typography variant="body2" color="#c2c2c2" sx={{ fontSize: 12 }}>
              {tEvent('ticket-status.limited-tickets-left')}
            </Typography>
          </Collapse>
          <Collapse
            in={isWithinSalePeriod && ticket.quantityInCart === ticket.quantityLeft}
            timeout={{ appear: 300, enter: 300, exit: 0 }}
            mountOnEnter
            unmountOnExit
          >
            <Typography variant="body2" color="#c2c2c2" sx={{ fontSize: 12 }}>
              {tEvent('ticket-status.last-one')}
            </Typography>
          </Collapse>
          <Collapse
            in={isWithinSalePeriod && ticket.quantityInCart === ticket.maxPerUser}
            timeout={{ appear: 300, enter: 300, exit: 0 }}
            mountOnEnter
            unmountOnExit
          >
            <Typography variant="body2" color="#c2c2c2" sx={{ fontSize: 12 }}>
              {tEvent('ticket-status.max-tickets', { maxPerUser: ticket.maxPerUser })}
            </Typography>
          </Collapse>
          <Collapse
            in={
              isWithinSalePeriod && cartLocks && cartLocks.some((lock) => lock.itemId === ticket.id)
            }
            timeout={{ appear: 300, enter: 300, exit: 0 }}
            mountOnEnter
            unmountOnExit
          >
            <Typography variant="body2" color="#c2c2c2" sx={{ fontSize: 12 }}>
              {tEvent('ticket-status.lock-checkout', { maxPerUser: ticket.maxPerUser })}
            </Typography>
          </Collapse>
        </Stack>

        {/* Conditional rendering of either TicketStatus or IncrementButton */}
        {!isWithinSalePeriod ? (
          <TicketStatus
            from={ticket.from ? String(ticket.from) : ''}
            to={ticket.to ? String(ticket.to) : ''}
          />
        ) : (
          <Controller
            name={`tickets.${index}.quantityInCart`}
            control={control}
            render={({ field }) => (
              <IncrementButton
                quantity={field.value}
                onIncrease={() => handleIncrease(field, index)}
                onDecrease={() => handleDecrease(field, index)}
                disabledDecrease={field.value <= 0}
              />
            )}
          />
        )}
      </Stack>
    </Stack>
  );
};

export default TicketAvailable;
